import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './Loader.css'
import './SuccessScreen.css'
import QRCode from 'react-qr-code';
import Layout from './Layout';
import { BackendAPI } from '../services/BackendAPI';

export type ProductType = 'evcharging' | 'smartschank';

export type SuccessScreenProps = {
    qrCode: string;
    journeyId: string;
    cashboxid: string;
    accesstoken: string;
    productType: ProductType;
    done: () => void;
}

const ACTION_MESSAGES: Record<ProductType, string> = {
    evcharging: 'Please plug in and start charging your vehicle.',
    smartschank: 'Please grab your drink from the Smartschank.',
};

const COUNTDOWN_SECONDS = 5;

const NUMPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

const COUNTRY_CODES = [
    { code: '0043', display: '+43', flag: '🇦🇹', label: 'AT' },
    { code: '0049', display: '+49', flag: '🇩🇪', label: 'DE' },
    { code: '0041', display: '+41', flag: '🇨🇭', label: 'CH' },
    { code: '0039', display: '+39', flag: '🇮🇹', label: 'IT' },
    { code: '0033', display: '+33', flag: '🇫🇷', label: 'FR' },
    { code: '0031', display: '+31', flag: '🇳🇱', label: 'NL' },
    { code: '00420', display: '+420', flag: '🇨🇿', label: 'CZ' },
    { code: '0036', display: '+36', flag: '🇭🇺', label: 'HU' },
    { code: '00386', display: '+386', flag: '🇸🇮', label: 'SI' },
    { code: '00385', display: '+385', flag: '🇭🇷', label: 'HR' },
    { code: '0048', display: '+48', flag: '🇵🇱', label: 'PL' },
    { code: '0044', display: '+44', flag: '🇬🇧', label: 'GB' },
];

const SuccessScreen = (props: SuccessScreenProps) => {
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [contactInfo, setContactInfo] = useState('');
    const [countryCode, setCountryCode] = useState('0043');
    const [localNumber, setLocalNumber] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

    useEffect(() => {
        if (confirmationMessage === null) return;

        if (countdown <= 0) {
            props.done();
            return;
        }

        const timer = setTimeout(() => {
            setCountdown(prev => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [confirmationMessage, countdown, props]);

    const showConfirmation = (message: string) => {
        setConfirmationMessage(message);
        setCountdown(COUNTDOWN_SECONDS);
    };

    const handleNumpadPress = (key: string) => {
        if (key === '⌫') {
            setLocalNumber(prev => prev.length > 0 ? prev.slice(0, -1) : prev);
        } else if (key !== '') {
            setLocalNumber(prev => prev + key);
        }
    };

    const fullPhoneNumber = countryCode + localNumber.replace(/^0+/, '');
    const displayCode = COUNTRY_CODES.find(c => c.code === countryCode)?.display ?? countryCode;

    const resetSms = () => {
        setShowSmsModal(false);
        setCountryCode('0043');
        setLocalNumber('');
    };

    const handleDeliveryMethod = async (method: 'email' | 'sms') => {
        setIsSending(true);
        try {
            const target = method === 'sms' ? fullPhoneNumber : contactInfo;
            const response = await new BackendAPI().handleDeliveryMethod(method, props.journeyId, target);
            if (response.ok) {
                setShowEmailModal(false);
                resetSms();
                setContactInfo('');
                showConfirmation(method === 'email' ? 'Email sent!' : 'SMS sent!');
            }
        } catch (error) {
            console.error('Error setting delivery method:', error);
        } finally {
            setIsSending(false);
        }
    };

    if (confirmationMessage) {
        return (
            <Layout>
                <div className="confirmation-screen">
                    <div className="confirmation-icon">&#10003;</div>
                    <div className="confirmation-message">{confirmationMessage}</div>
                    <div className="confirmation-action">{ACTION_MESSAGES[props.productType]}</div>
                    <div className="confirmation-countdown">
                        Returning in {countdown}...
                    </div>
                    <div className="confirmation-progress-track">
                        <div
                            className="confirmation-progress-bar"
                            style={{ width: `${(countdown / COUNTDOWN_SECONDS) * 100}%` }}
                        />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="success-screen">
                <div className="success-screen-qr">
                    <a href={props.qrCode} target="_blank" rel="noreferrer">
                        <QRCode value={props.qrCode} />
                    </a>
                </div>
                <div className="success-screen-actions">
                    <Button
                        size="lg"
                        className="w-100"
                        onClick={() => setShowEmailModal(true)}
                    >
                        E-Mail
                    </Button>
                    <Button
                        size="lg"
                        className="w-100"
                        onClick={() => setShowSmsModal(true)}
                    >
                        SMS
                    </Button>
                    <Button
                        size="lg"
                        className="w-100"
                        onClick={() => showConfirmation('Thank you!')}
                    >
                        Done
                    </Button>
                </div>
            </div>

            {/* Email Modal */}
            <Modal
                show={showEmailModal}
                onHide={() => { setShowEmailModal(false); setContactInfo(''); }}
                fullscreen
                className="email-modal"
            >
                <Modal.Body className="email-modal-body">
                    <div className="email-modal-content">
                        <div className="email-modal-label">Enter Email Address</div>
                        <input
                            className="email-modal-input"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            autoFocus
                            placeholder="name@example.com"
                            value={contactInfo}
                            onChange={(e) => setContactInfo(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && contactInfo.includes('@')) {
                                    handleDeliveryMethod('email');
                                }
                            }}
                        />
                    </div>
                    <div className="email-modal-actions">
                        <button
                            className="email-modal-cancel"
                            onClick={() => { setShowEmailModal(false); setContactInfo(''); }}
                        >
                            Cancel
                        </button>
                        <button
                            className="email-modal-send"
                            disabled={!contactInfo.includes('@') || isSending}
                            onClick={() => handleDeliveryMethod('email')}
                        >
                            {isSending ? 'Sending...' : 'Send Email'}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* SMS Numpad */}
            <Modal
                show={showSmsModal}
                onHide={resetSms}
                fullscreen
                className="sms-numpad-modal"
            >
                <Modal.Body className="sms-numpad-body">
                    <div className="sms-numpad-display">
                        <div className="sms-numpad-label">Select Country &amp; Enter Number</div>
                        <div className="sms-numpad-number">
                            <span className="sms-numpad-prefix">{displayCode}</span>
                            {localNumber || <span className="sms-numpad-placeholder">Phone number</span>}
                        </div>
                    </div>
                    <div className="sms-country-picker">
                        {COUNTRY_CODES.map((c) => (
                            <button
                                key={c.code}
                                className={`sms-country-btn ${countryCode === c.code ? 'sms-country-btn--active' : ''}`}
                                onClick={() => setCountryCode(c.code)}
                            >
                                <span className="sms-country-flag">{c.flag}</span>
                                <span className="sms-country-code">{c.display}</span>
                            </button>
                        ))}
                    </div>
                    <div className="sms-numpad-grid">
                        {NUMPAD_KEYS.map((key, i) => (
                            key === '' ? (
                                <div key={i} />
                            ) : (
                                <button
                                    key={key}
                                    className={`sms-numpad-key ${key === '⌫' ? 'sms-numpad-key--backspace' : ''}`}
                                    onClick={() => handleNumpadPress(key)}
                                >
                                    {key}
                                </button>
                            )
                        ))}
                    </div>
                    <div className="sms-numpad-actions">
                        <button
                            className="sms-numpad-cancel"
                            onClick={resetSms}
                        >
                            Cancel
                        </button>
                        <button
                            className="sms-numpad-send"
                            disabled={localNumber.length < 4 || isSending}
                            onClick={() => handleDeliveryMethod('sms')}
                        >
                            {isSending ? 'Sending...' : 'Send SMS'}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </Layout>
    );
};

export default SuccessScreen;